import { create } from 'zustand';
import {
  CustomNode,
  GroupedNodes,
  NodeType,
  NodeInputType,
} from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import { v4 as uuidv4 } from 'uuid';
import {
  buildCatalogIdentitySet,
  getCatalogNodeIdentityKey,
} from '@/pages/workflow/helper/catalog-node-identity';
import { FileUploadResponse } from '@/services/workflow/upload-file';
import {
  getWorkflowGraph,
  getConnectedComponents,
  detectCycle,
} from '@/pages/workflow/helper/graph-analysis';

export interface UnsupportedNodeError {
  id: string;
  nodeId: string;
  nodeName: string;
  nodeType: string;
  nodeSubType: string;
  position: { x: number; y: number };
}

export interface MissingFileError {
  id: string;
  nodeId: string;
  nodeName: string;
  fileName: string;
  fieldId: string;
}

export interface FieldValidationError {
  id: string;
  nodeId: string;
  nodeName: string;
  fieldId: string;
  fieldName: string;
  message: string;
}

export interface MultipleWorkflowsError {
  id: string;
  /** Number of disconnected components detected. */
  componentCount: number;
  /** Node IDs grouped by connected component. */
  components: string[][];
}

export interface CyclicGraphError {
  id: string;
  /** IDs of nodes that participate in at least one cycle. */
  cycleNodeIds: string[];
}

export interface ValidateFieldsOptions {
  // An empty File Input is only a problem when the workflow runs; on open it
  // is legitimate saved state (unused slots, promoted copies with files nulled).
  skipFileInputs?: boolean;
}

/**
 * Whether a catalog `default` would stand in for an empty value. The backend
 * (`BaseNode.__get_param_value`) uses the default whenever the value is
 * `None` or `""`, so a required field with a non-empty default is never
 * missing. Without this, every node carrying hidden/advanced defaults
 * (`config_version: "1.0"`, Validate's `error_code`, JSON Input's
 * `on_invalid`) read "Configure required field" while fully configured.
 */
export const hasUsableDefault = (defaultValue: unknown): boolean => {
  if (defaultValue === undefined || defaultValue === null) return false;
  if (typeof defaultValue === 'string') return defaultValue.trim() !== '';
  if (Array.isArray(defaultValue)) return defaultValue.length > 0;
  if (typeof defaultValue === 'object') {
    return Object.keys(defaultValue).length > 0;
  }
  return true;
};

/** Emptiness for a `JsonInput` value: `[]`, `{}`, `""` and `null` are empty. */
export const isEmptyJsonValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    try {
      return isEmptyJsonValue(JSON.parse(trimmed));
    } catch {
      return false;
    }
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

interface WorkflowValidationState {
  unsupportedNodes: UnsupportedNodeError[];
  missingFiles: MissingFileError[];
  fieldErrors: FieldValidationError[];
  /** True when import validation was skipped because the node list was empty. */
  nodeListUnavailable: boolean;
  /** Set when the canvas contains more than one disconnected workflow. */
  multipleWorkflows: MultipleWorkflowsError | null;
  /** Set when the workflow graph contains a cycle (not a DAG). */
  cyclicGraph: CyclicGraphError | null;

  addUnsupportedNode: (error: UnsupportedNodeError) => void;
  addMissingFile: (error: MissingFileError) => void;
  removeUnsupportedNode: (errorId: string) => void;
  removeMissingFile: (errorId: string) => void;
  removeFieldError: (errorId: string) => void;
  clearFieldError: (nodeId: string, fieldId: string) => void;
  clearMissingFile: (nodeId: string, fieldId: string) => void;
  clearAllErrors: () => void;
  clearImportErrors: () => void;
  clearFieldErrors: () => void;
  setMultipleWorkflowsError: (error: MultipleWorkflowsError | null) => void;
  setCyclicGraphError: (error: CyclicGraphError | null) => void;
  validateFields: (
    nodes: CustomNode[],
    options?: ValidateFieldsOptions,
  ) => void;
  hasErrors: () => boolean;
  canRunWorkflow: () => boolean;
  getErrorCount: () => number;
}

export const useWorkflowValidationStore = create<WorkflowValidationState>(
  (set, get) => ({
    unsupportedNodes: [],
    missingFiles: [],
    fieldErrors: [],
    nodeListUnavailable: false,
    multipleWorkflows: null,
    cyclicGraph: null,

    addUnsupportedNode: (error: UnsupportedNodeError) =>
      set((state) => ({
        unsupportedNodes: [...state.unsupportedNodes, error],
      })),

    addMissingFile: (error: MissingFileError) =>
      set((state) => ({
        missingFiles: [...state.missingFiles, error],
      })),

    removeUnsupportedNode: (errorId: string) =>
      set((state) => ({
        unsupportedNodes: state.unsupportedNodes.filter(
          (error) => error.id !== errorId,
        ),
      })),

    removeMissingFile: (errorId: string) =>
      set((state) => ({
        missingFiles: state.missingFiles.filter(
          (error) => error.id !== errorId,
        ),
      })),

    removeFieldError: (errorId: string) =>
      set((state) => ({
        fieldErrors: state.fieldErrors.filter((error) => error.id !== errorId),
      })),

    clearFieldError: (nodeId: string, fieldId: string) =>
      set((state) => ({
        fieldErrors: state.fieldErrors.filter(
          (error) => !(error.nodeId === nodeId && error.fieldId === fieldId),
        ),
      })),

    clearMissingFile: (nodeId: string, fieldId: string) =>
      set((state) => ({
        missingFiles: state.missingFiles.filter(
          (error) => !(error.nodeId === nodeId && error.fieldId === fieldId),
        ),
      })),

    setMultipleWorkflowsError: (error) => set({ multipleWorkflows: error }),

    setCyclicGraphError: (error) => set({ cyclicGraph: error }),

    clearAllErrors: () =>
      set({
        unsupportedNodes: [],
        missingFiles: [],
        fieldErrors: [],
        nodeListUnavailable: false,
        multipleWorkflows: null,
        cyclicGraph: null,
      }),

    clearImportErrors: () =>
      set({
        unsupportedNodes: [],
        missingFiles: [],
        nodeListUnavailable: false,
      }),

    clearFieldErrors: () =>
      set({
        fieldErrors: [],
      }),

    validateFields: (nodes: CustomNode[], options?: ValidateFieldsOptions) => {
      const errors: FieldValidationError[] = [];

      nodes.forEach((node) => {
        const nodeStruct = node.data.struct;
        if (!nodeStruct || !nodeStruct.params) return;

        if (nodeStruct.node_type === 'tool') {
          // Validate collection/dataset scope on list tool nodes
          const listToolIds = [
            'list_unstructured_collections',
            'list_structured_datasets',
          ];
          const scopeFieldMap: Record<string, string> = {
            list_unstructured_collections: 'collection_scope',
            list_structured_datasets: 'dataset_scope',
          };
          const toolNodeId = nodeStruct.node_id ?? '';

          if (listToolIds.includes(toolNodeId)) {
            const expectedField = scopeFieldMap[toolNodeId];
            const scopeParam = nodeStruct.params?.find(
              (p) => p.field_id === expectedField,
            );

            // Old node (no scope param at all) or new node with empty scope
            const hasSelections =
              scopeParam &&
              Array.isArray(scopeParam.value) &&
              scopeParam.value.length > 0;

            if (!hasSelections) {
              const isOldNode = !scopeParam;
              errors.push({
                id: `${node.id}-${expectedField}`,
                nodeId: node.id,
                nodeName:
                  nodeStruct.user_config_name ||
                  nodeStruct.display_name ||
                  'Unknown Tool',
                fieldId: expectedField,
                fieldName: isOldNode
                  ? 'Outdated Tool Node'
                  : 'Collection Scope',
                message: isOldNode
                  ? 'This tool node was added before the collections update. Remove it and re-add it from the catalog to configure which collections the agent can access.'
                  : 'No collections/datasets selected. Click this tool node and choose which ones the agent should use. If the dropdown is empty, remove this node and re-add it from the catalog to load available options.',
              });
            }
          }
          return;
        }

        nodeStruct.params.forEach((param) => {
          if (!param.required) return;

          if (
            options?.skipFileInputs &&
            nodeStruct.node_sub_type === 'file_input' &&
            param.display_modal === NodeInputType.FILE_UPLOAD
          ) {
            // A skipped field is not re-judged, so a Run-time error already
            // recorded for it must survive this pass rather than be dropped.
            const existing = get().fieldErrors.find(
              (error) =>
                error.nodeId === node.id && error.fieldId === param.field_id,
            );
            if (existing) errors.push(existing);
            return;
          }

          if (param.bindable_config && param.bindable_config.is_bound) {
            return;
          }

          // A catalog default fills an empty value at run time, so the
          // field is not missing (see hasUsableDefault). Only an empty
          // value with no usable default is a real gap.
          const valueIsEmpty =
            param.value === undefined ||
            param.value === null ||
            (typeof param.value === 'string' && param.value.trim() === '');
          if (valueIsEmpty && hasUsableDefault(param.default)) {
            return;
          }

          const validationResult = (() => {
            if (param.value === undefined || param.value === null)
              return { valid: false, message: 'This field is required' };

            switch (param.display_modal) {
              case NodeInputType.JSON_INPUT:
                return isEmptyJsonValue(param.value)
                  ? { valid: false, message: 'This field is required' }
                  : { valid: true };

              case NodeInputType.TEXT_BOX:
              case NodeInputType.TEXT_AREA:
              case NodeInputType.SECRET_TEXT_INPUT:
                return String(param.value).trim() !== ''
                  ? { valid: true }
                  : { valid: false, message: 'This field is required' };

              case NodeInputType.INTEGER:
              case NodeInputType.FLOAT: {
                const trimmed = String(param.value).trim();
                if (trimmed === '')
                  return { valid: false, message: 'This field is required' };
                if (isNaN(Number(trimmed)))
                  return {
                    valid: false,
                    message: 'Must be a valid numeric value',
                  };
                return { valid: true };
              }

              case NodeInputType.BOOLEAN:
                return { valid: true };

              case NodeInputType.LIST:
                return (
                  Array.isArray(param.value)
                    ? param.value.length > 0
                    : String(param.value).trim() !== ''
                )
                  ? { valid: true }
                  : { valid: false, message: 'This field is required' };

              case NodeInputType.OBJECT:
                if (typeof param.value === 'object' && param.value !== null) {
                  return Object.keys(param.value).length > 0
                    ? { valid: true }
                    : { valid: false, message: 'This field is required' };
                }
                return { valid: false, message: 'This field is required' };

              case NodeInputType.MULTI_SELECT:
                return String(param.value).trim() !== ''
                  ? { valid: true }
                  : { valid: false, message: 'This field is required' };

              default:
                return String(param.value).trim() !== ''
                  ? { valid: true }
                  : { valid: false, message: 'This field is required' };
            }
          })();

          if (!validationResult.valid) {
            errors.push({
              id: `${node.id}-${param.field_id}`,
              nodeId: node.id,
              nodeName:
                nodeStruct.user_config_name ||
                nodeStruct.display_name ||
                'Unknown Node',
              fieldId: param.field_id,
              fieldName: param.display_name || param.field_id,
              message: validationResult.message ?? 'This field is required',
            });
          }
        });
      });

      set({ fieldErrors: errors });
    },

    hasErrors: () => {
      const state = get();
      return (
        state.unsupportedNodes.length > 0 ||
        state.missingFiles.length > 0 ||
        state.fieldErrors.length > 0 ||
        state.nodeListUnavailable ||
        state.multipleWorkflows !== null ||
        state.cyclicGraph !== null
      );
    },

    canRunWorkflow: () => !get().hasErrors(),

    getErrorCount: () => {
      const state = get();
      return (
        state.unsupportedNodes.length +
        state.missingFiles.length +
        state.fieldErrors.length +
        (state.nodeListUnavailable ? 1 : 0) +
        (state.multipleWorkflows !== null ? 1 : 0) +
        (state.cyclicGraph !== null ? 1 : 0)
      );
    },
  }),
);

/**
 * Validates graph topology: detects multiple disconnected workflows and cycles.
 * Writes results directly into the validation store.
 * Should be called on every run attempt alongside validateFields().
 */
export function validateGraphStructure(
  nodes: CustomNode[],
  edges: CustomEdge[],
): void {
  const { setMultipleWorkflowsError, setCyclicGraphError } =
    useWorkflowValidationStore.getState();

  const { nodes: wfNodes, edges: wfEdges } = getWorkflowGraph(nodes, edges);

  const components = getConnectedComponents(wfNodes, wfEdges);
  if (components.length > 1) {
    setMultipleWorkflowsError({
      id: uuidv4(),
      componentCount: components.length,
      components,
    });
  } else {
    setMultipleWorkflowsError(null);
  }

  const { hasCycle, cycleNodeIds } = detectCycle(wfNodes, wfEdges);
  if (hasCycle) {
    setCyclicGraphError({ id: uuidv4(), cycleNodeIds });
  } else {
    setCyclicGraphError(null);
  }
}

// download_url is a short-lived signed link that several backend write paths
// omit and the runtime re-signs from s3_key, so only a durable file identity
// proves the upload exists. The export scrub keeps just filename/size, which
// correctly stays "missing" until the importer re-uploads.
export function hasPersistedFile(value: FileUploadResponse): boolean {
  return Boolean(value.file?.file_id || value.file?.s3_key || value.s3_key);
}

/**
 * Validates workflow nodes against the catalog and writes unsupported-node /
 * missing-file errors into the validation store.
 *
 * Callers are responsible for clearing stale import errors before calling and
 * for running `validateFields()` afterward if field errors also need refreshing.
 *
 * SelectNode is always skipped — it is an ephemeral canvas placeholder that is
 * filtered out before save. StickyNoteNode and GroupNode are also skipped —
 * both are canvas-only annotations that are persisted but not part of the
 * executable workflow, and their structs are synthesized on the frontend
 * rather than coming from the backend catalog, so they'd never match
 * availableCatalogIdentities.
 */
export function validateImportErrors(
  nodes: CustomNode[],
  groupedNodes: GroupedNodes,
): void {
  const emptyNodeList = Object.keys(groupedNodes).length === 0;
  if (emptyNodeList) {
    useWorkflowValidationStore.setState({ nodeListUnavailable: true });
  }

  const { addUnsupportedNode, addMissingFile } =
    useWorkflowValidationStore.getState();

  const availableCatalogIdentities = buildCatalogIdentitySet(groupedNodes);

  nodes.forEach((node) => {
    const struct = node.data?.struct;
    if (!struct) return;

    if (
      node.type === NodeType.SelectNode ||
      node.type === NodeType.StickyNoteNode ||
      node.type === NodeType.GroupNode
    ) {
      return;
    }

    if (
      !emptyNodeList &&
      !availableCatalogIdentities.has(getCatalogNodeIdentityKey(struct))
    ) {
      addUnsupportedNode({
        id: uuidv4(),
        nodeId: node.id,
        nodeName: struct.user_config_name || struct.display_name,
        nodeType: struct.node_type,
        nodeSubType: struct.node_sub_type,
        position: node.position,
      });
    }

    if (struct.node_sub_type === 'file_input') {
      const fileUploadField = struct.params?.find(
        (param) => param.display_modal === NodeInputType.FILE_UPLOAD,
      );
      if (fileUploadField?.value) {
        const fileValue = fileUploadField.value as FileUploadResponse;
        if (!hasPersistedFile(fileValue)) {
          addMissingFile({
            id: uuidv4(),
            nodeId: node.id,
            nodeName: struct.user_config_name || struct.display_name,
            fileName: fileValue.file?.filename ?? '',
            fieldId: fileUploadField.field_id,
          });
        }
      }
    }
  });
}
